/**
 * The Instagram Story card: a 1080×1920 PNG of the verdict, drawn on a canvas
 * rather than screenshotted, so fonts and the radar come out the same on every
 * phone. It takes the numbers the Verdict screen already shows and never
 * recomputes them.
 */

import { DIMENSIONS, type DimensionKey } from "./content";
import { SHARE_HOST } from "./share";
import { archivoBlack, hind, plexMono } from "@/components/aiCheck/fonts";

export type CardData = {
  /** First name only, or "" to leave it off. */
  name: string;
  score: number;
  band: string;
  dims: Record<DimensionKey, number>;
};

const W = 1080;
const H = 1920;

/* The theme's oklch tokens as hex: older iOS canvases don't take oklch. */
const C = {
  bg: "#0b0a07",
  surface: "#161510",
  fg: "#f6f5f1",
  muted: "#9a9890",
  gold: "#f2bb31",
  goldSoft: "#f7db98",
  gold1: "#f8dc86",
  gold2: "#e99b2a",
  mint1: "#84f8d2",
  mint2: "#00c2a5",
  hairline: "rgba(255,255,255,0.12)",
};

/* next/font renames the families, and a canvas can't read CSS variables, so use the real names. */
const DISPLAY = `${archivoBlack.style.fontFamily}, "Archivo Black", system-ui, sans-serif`;
const SANS = `${hind.style.fontFamily}, "Hind", system-ui, sans-serif`;
const MONO = `${plexMono.style.fontFamily}, "IBM Plex Mono", ui-monospace, monospace`;

/** Webfonts are lazy: draw before they load and the canvas silently uses a fallback. */
async function fontsReady() {
  if (typeof document === "undefined" || !document.fonts) return;
  await Promise.all([
    document.fonts.load(`120px ${DISPLAY}`),
    document.fonts.load(`600 40px ${SANS}`),
    document.fonts.load(`500 30px ${MONO}`),
  ]).catch(() => undefined);
}

function gradient(
  ctx: CanvasRenderingContext2D,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  a: string,
  b: string,
) {
  const g = ctx.createLinearGradient(x0, y0, x1, y1);
  g.addColorStop(0, a);
  g.addColorStop(1, b);
  return g;
}

/** Shrinks a line until it fits, so long names never run off the card. */
function fitFont(
  ctx: CanvasRenderingContext2D,
  text: string,
  family: string,
  size: number,
  max: number,
) {
  let s = size;
  ctx.font = `${s}px ${family}`;
  while (s > 40 && ctx.measureText(text).width > max) {
    s -= 4;
    ctx.font = `${s}px ${family}`;
  }
}

function spaced(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  spacing: number,
) {
  /* letterSpacing on canvas is too new to rely on; space the glyphs by hand. */
  const chars = [...text];
  const total =
    chars.reduce((w, ch) => w + ctx.measureText(ch).width, 0) + spacing * (chars.length - 1);
  let cx = x - total / 2;
  ctx.textAlign = "left";
  for (const ch of chars) {
    ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + spacing;
  }
  ctx.textAlign = "center";
}

/** Same geometry as `Radar` in kit.tsx: first axis straight up, rings at a third. */
function radar(ctx: CanvasRenderingContext2D, data: CardData, cx: number, cy: number, R: number) {
  const n = DIMENSIONS.length;
  const pt = (i: number, r: number) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as const;
  };
  const path = (r: (_i: number) => number) => {
    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const [x, y] = pt(i, r(i));
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
  };

  ctx.strokeStyle = C.hairline;
  ctx.lineWidth = 3;
  for (const f of [0.33, 0.66, 1]) {
    path(() => R * f);
    ctx.stroke();
  }
  for (let i = 0; i < n; i++) {
    const [x, y] = pt(i, R);
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(x, y);
    ctx.stroke();
  }

  path((i) => Math.max(0.06, data.dims[DIMENSIONS[i]!.k] / DIMENSIONS[i]!.max) * R);
  ctx.fillStyle = "rgba(242,187,49,0.35)";
  ctx.fill();
  ctx.strokeStyle = C.gold;
  ctx.lineWidth = 8;
  ctx.lineJoin = "round";
  ctx.stroke();

  /* Labels carry the numbers too, so the chart never relies on shape or colour alone. */
  ctx.textAlign = "center";
  /* Each label hugs its own corner: stacked above a top corner, below a bottom one. */
  DIMENSIONS.forEach((d, i) => {
    const [x, y] = pt(i, R);
    const above = y < cy;
    const labelY = above ? y - 84 : y + 60;
    const valueY = above ? y - 30 : y + 110;
    ctx.fillStyle = C.muted;
    ctx.font = `500 30px ${MONO}`;
    spaced(ctx, d.label.toUpperCase(), x, labelY, 4);
    ctx.fillStyle = C.goldSoft;
    ctx.font = `600 44px ${SANS}`;
    ctx.fillText(`${data.dims[d.k]}/${d.max}`, x, valueY);
  });
}

/** Draws the card. Kept apart from the export so it can be called on any 1080×1920 context. */
export function drawShareCard(ctx: CanvasRenderingContext2D, data: CardData) {
  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, W, H);

  /* The app's stage glow: gold from the top, mint from the bottom corner. */
  const top = ctx.createRadialGradient(W / 2, -200, 0, W / 2, -200, 1300);
  top.addColorStop(0, "rgba(242,187,49,0.20)");
  top.addColorStop(1, "rgba(242,187,49,0)");
  ctx.fillStyle = top;
  ctx.fillRect(0, 0, W, H);
  const corner = ctx.createRadialGradient(W, H, 0, W, H, 1100);
  corner.addColorStop(0, "rgba(45,232,179,0.14)");
  corner.addColorStop(1, "rgba(45,232,179,0)");
  ctx.fillStyle = corner;
  ctx.fillRect(0, 0, W, H);

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";

  /* Top 250px stays clear for Instagram's own bar. */
  ctx.fillStyle = C.muted;
  ctx.font = `500 32px ${MONO}`;
  spaced(ctx, "MILES · AI-READY CHECK", W / 2, 290, 6);

  ctx.fillStyle = C.fg;
  ctx.font = `600 54px ${SANS}`;
  ctx.fillText(data.name ? `${data.name} is` : "I'm", W / 2, 400);

  fitFont(ctx, data.band, DISPLAY, 132, W - 140);
  ctx.fillStyle = gradient(ctx, 0, 430, 0, 565, C.mint1, C.mint2);
  ctx.fillText(data.band, W / 2, 545);

  ctx.font = `150px ${DISPLAY}`;
  ctx.fillStyle = gradient(ctx, 0, 600, 0, 745, C.gold1, C.gold2);
  const score = String(data.score);
  const scoreW = ctx.measureText(score).width;
  ctx.font = `600 50px ${SANS}`;
  const outOf = " / 100";
  const outW = ctx.measureText(outOf).width;
  const left = W / 2 - (scoreW + outW) / 2;
  ctx.textAlign = "left";
  ctx.font = `150px ${DISPLAY}`;
  ctx.fillText(score, left, 735);
  ctx.fillStyle = C.muted;
  ctx.font = `600 50px ${SANS}`;
  ctx.fillText(outOf, left + scoreW, 735);
  ctx.textAlign = "center";

  radar(ctx, data, W / 2, 1130, 230);

  ctx.fillStyle = C.fg;
  ctx.font = `64px ${DISPLAY}`;
  ctx.fillText("How AI-ready are you?", W / 2, 1465);

  /* 1495–1630 is left empty for the Link sticker; the address sits below as plain text. */
  ctx.fillStyle = C.muted;
  ctx.font = `500 34px ${MONO}`;
  ctx.fillText(SHARE_HOST, W / 2, 1660);
}

/** Renders the card to a PNG file ready for the share sheet. */
export async function renderShareCard(data: CardData): Promise<File> {
  await fontsReady();
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unavailable");
  drawShareCard(ctx, data);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) throw new Error("could not export the card");
  return new File([blob], "my-ai-readiness.png", { type: "image/png" });
}

/** "Vishwanath Murthy" → "Vishwanath". The card only ever carries the first name. */
export const firstName = (full: string) => full.trim().split(/\s+/)[0] ?? "";
